/**
 * DAFNE Insulin Configuration
 *
 * Insulin activity parameters, IOB calculations, and correction formulas.
 * Based on OZ DAFNE guidelines for rapid-acting insulin.
 *
 * Last reviewed: February 2026
 */

// =============================================================================
// INSULIN ON BOARD (IOB) PARAMETERS
// =============================================================================

export const IOB_CONFIG = {
  /** Duration of quick-acting insulin activity (hours) */
  QA_DURATION_HOURS: 4,

  /** Duration window for querying recent entries for IOB (hours) */
  IOB_QUERY_WINDOW_HOURS: 4,

  /** Duration window for loading entries context (hours) */
  ENTRY_LOAD_WINDOW_HOURS: 24,
} as const;

// =============================================================================
// DEFAULT CORRECTION FACTOR
// =============================================================================

export const CORRECTION_CONFIG = {
  /** Default ISF: null means user must set from their DAFNE course */
  DEFAULT_CORRECTION_FACTOR: null as number | null,

  /** Minimum correction factor (most sensitive) */
  MIN_CORRECTION_FACTOR: 0.5,

  /** Maximum correction factor (least sensitive) */
  MAX_CORRECTION_FACTOR: 10.0,
};

// =============================================================================
// OZDAFNE EFFECT RANGES
// How much 1 unit of insulin or 1 CP affects blood glucose
// =============================================================================

export const EFFECT_RANGES = {
  /**
   * OZDAFNE: 1 unit of quick-acting insulin changes BG by 2.0-3.0 mmol/L
   * - 1 extra unit lowers BG by 2.0-3.0 mmol/L
   * - 1 unit less raises BG by 2.0-3.0 mmol/L
   */
  INSULIN: {
    MIN: 2.0,
    MAX: 3.0,
    TYPICAL: 2.5,
    DESCRIPTION: "1 unit QA changes BG by 2.0-3.0 mmol/L",
  },

  /**
   * OZDAFNE: 1 CP (10g carbs) raises BG by 2.0-3.0 mmol/L
   */
  CARBOHYDRATE: {
    MIN: 2.0,
    MAX: 3.0,
    TYPICAL: 2.5,
    DESCRIPTION: "1 CP (10g carbs) raises BG by 2.0-3.0 mmol/L",
  },
} as const;

// =============================================================================
// EXERCISE ADJUSTMENT PERCENTAGES
// =============================================================================

export const EXERCISE_ADJUSTMENTS = {
  /** Pre-exercise QA reduction percentages by intensity */
  PRE_EXERCISE: {
    LIGHT: 15, // Base percentage per hour
    MODERATE: 30,
    VIGOROUS: 50,
  },

  /** Post-exercise QA reduction percentages by intensity */
  POST_EXERCISE: {
    LIGHT: 10,
    MODERATE: 20,
    VIGOROUS: 30,
  },

  /** Maximum QA reduction caps */
  CAPS: {
    PRE_EXERCISE_MAX: 50,
    POST_EXERCISE_MAX: 30,
  },

  /** Extra carbs alternatives (grams per hour of exercise) */
  EXTRA_CARBS: {
    PRE_LIGHT: 10,
    PRE_MODERATE: 15,
    PRE_VIGOROUS: 25,
    POST_LIGHT: 5,
    POST_MODERATE: 10,
    POST_VIGOROUS: 15,
    PRE_MAX: 40,
    POST_MAX: 20,
  },
} as const;

// =============================================================================
// DEFAULT CARB:INSULIN RATIOS
// STORAGE FORMAT: grams per unit (e.g., 2.5 = 1 unit covers 2.5g carbs)
// DISPLAY FORMAT: units per CP (e.g., 4 = 4 units per 10g carbs)
// User MUST set these - null means not configured
// =============================================================================

export const DEFAULT_CP_RATIOS = {
  /** Breakfast ratio - must be set by user from their DAFNE course */
  B: null as number | null,

  /** Lunch ratio */
  L: null as number | null,

  /** Evening meal ratio */
  EM: null as number | null,

  /** Bedtime ratio */
  BT: null as number | null,

  /** 3AM check ratio */
  "3AM": null as number | null,
};

// =============================================================================
// CP RATIO UTILITIES - SINGLE SOURCE OF TRUTH
// Storage: grams per unit | Display: units per CP (10g)
// =============================================================================

/**
 * Convert storage format (grams per unit) to display format (units per CP).
 * 1 CP = 10g carbs (DAFNE standard)
 *
 * Example: stored 2.5 grams/unit → display 4 units/CP
 */
export function gramsPerUnitToUnitsPerCP(
  gramsPerUnit: number | null | undefined,
): number {
  if (!gramsPerUnit || gramsPerUnit <= 0) return 0;
  return 10 / gramsPerUnit;
}

/**
 * Convert display format (units per CP) to storage format (grams per unit).
 *
 * Example: display 4 units/CP → stored 2.5 grams/unit
 */
export function unitsPerCPToGramsPerUnit(unitsPerCP: number): number {
  if (unitsPerCP <= 0) return 0;
  return 10 / unitsPerCP;
}

/**
 * Get units per CP for a period from settings, with optional rounding.
 */
export function getUnitsPerCPFromSettings(
  cpRatios: Record<string, number | null> | undefined,
  period: string,
  roundToHalf: boolean = true,
): number {
  const gramsPerUnit = cpRatios?.[period];
  const unitsPerCP = gramsPerUnitToUnitsPerCP(gramsPerUnit);
  return roundToHalf ? Math.round(unitsPerCP * 2) / 2 : unitsPerCP;
}

/**
 * Calculate quick-acting insulin dose for carbs.
 *
 * @param cpCount - Number of CP (10g portions)
 * @param cpRatios - Settings cpRatios object (stored as grams per unit)
 * @param period - Period key (B, L, EM, etc.)
 * @param doseIncrement - Rounding increment (default 0.5)
 * @returns Suggested insulin units, or 0 if ratio not configured
 */
export function calculateQADoseForCarbs(
  cpCount: number,
  cpRatios: Record<string, number | null> | undefined,
  period: string,
  doseIncrement: number = 0.5,
): number {
  const unitsPerCP = gramsPerUnitToUnitsPerCP(cpRatios?.[period]);
  if (!unitsPerCP) return 0;

  const rawDose = cpCount * unitsPerCP;
  return Math.round(rawDose / doseIncrement) * doseIncrement;
}

/**
 * Format ratio for display as "Xu/CP" or "X units/CP"
 */
export function formatRatioDisplay(
  gramsPerUnit: number | null | undefined,
  short: boolean = true,
): string {
  const unitsPerCP = gramsPerUnitToUnitsPerCP(gramsPerUnit);
  if (!unitsPerCP) return short ? "—" : "Not set";
  const rounded = Math.round(unitsPerCP * 2) / 2;
  const display = Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1);
  return short ? `${display}u/CP` : `${display} units/CP`;
}

// =============================================================================
// IOB CALCULATION FUNCTION
// =============================================================================

/**
 * Calculate remaining active insulin from a dose using linear decay model.
 *
 * DAFNE Formula: IOB = dose × (1 - hoursElapsed / duration)
 *
 * @param doseUnits - Original insulin dose in units
 * @param hoursElapsed - Hours since the dose was given
 * @param durationHours - Total activity duration (default: QA_DURATION_HOURS)
 * @returns Remaining active insulin in units
 */
export function calculateIOB(
  doseUnits: number,
  hoursElapsed: number,
  durationHours: number = IOB_CONFIG.QA_DURATION_HOURS,
): number {
  if (hoursElapsed >= durationHours || hoursElapsed < 0) return 0;

  // Linear decay model (simple DAFNE approximation)
  // More accurate models use exponential or bilinear curves
  const remaining = doseUnits * (1 - hoursElapsed / durationHours);
  return Math.max(0, remaining);
}

/**
 * Calculate correction dose using DAFNE formula.
 *
 * DAFNE Formula: correction = (currentBG - targetBG) / ISF - IOB
 *
 * @param currentBG - Current blood glucose (mmol/L)
 * @param targetBG - Target blood glucose (mmol/L)
 * @param correctionFactor - Insulin sensitivity factor (ISF)
 * @param activeIOB - Current insulin on board
 * @returns Suggested correction units (can be negative)
 */
export function calculateCorrectionDose(
  currentBG: number,
  targetBG: number,
  correctionFactor: number,
  activeIOB: number,
): number {
  const rawCorrection = (currentBG - targetBG) / correctionFactor;
  return rawCorrection - activeIOB;
}
